<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import Teams from "@/services/Teams";

export default {
  name: "FixturesStandings",
  components: { Layout, PageHeader },
  page: {
    title: "Standings",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  data() {
    return {
      standingsPayload: null,
      loading: false,
      error: null,
      tabIndex: 0,
      tablePerPage: 18,
      tablePages: {},
      tableFields: [
        { key: "rank", label: "#", sortable: true, thClass: "text-center", tdClass: "text-center" },
        { key: "team", label: "Team", sortable: false },
        { key: "matches_played", label: "PL", sortable: true, thClass: "text-center", tdClass: "text-center" },
        { key: "matches_won", label: "W", sortable: true, thClass: "text-center", tdClass: "text-center" },
        { key: "matches_drawn", label: "D", sortable: true, thClass: "text-center", tdClass: "text-center" },
        { key: "matches_lost", label: "L", sortable: true, thClass: "text-center", tdClass: "text-center" },
        { key: "goals_for", label: "GF", sortable: true, thClass: "text-center", tdClass: "text-center" },
        { key: "goals_against", label: "GA", sortable: true, thClass: "text-center", tdClass: "text-center" },
        { key: "goal_difference", label: "+/-", sortable: true, thClass: "text-center", tdClass: "text-center" },
        { key: "points", label: "Pts", sortable: true, thClass: "text-center", tdClass: "text-center fw-bold" },
      ],
    };
  },
  computed: {
    pageTitle() {
      return this.$t("menuitems.fixtures.list.fixtures_standings");
    },
    breadcrumbItems() {
      return [
        { text: "Admin", href: "/" },
        { text: "Fixtures", href: "/fixtures/matches_new" },
        {
          text: this.$t("menuitems.fixtures.list.fixtures_standings"),
          active: true,
        },
      ];
    },
    standingsGroups() {
      const raw = this.standingsPayload;
      if (!raw || typeof raw !== "object") return [];
      const keys = Object.keys(raw).filter((k) => k !== "competitions");
      const fromTeamTypes = keys
        .sort()
        .map((key) => ({
          key,
          label: this.formatTeamTypeLabel(key),
          competitions: Array.isArray(raw[key]) ? raw[key] : [],
        }))
        .filter((g) => g.competitions.length);

      if (fromTeamTypes.length) {
        return fromTeamTypes;
      }

      if (Array.isArray(raw.competitions) && raw.competitions.length) {
        return [
          {
            key: "all",
            label: "Competitions",
            competitions: raw.competitions,
          },
        ];
      }

      return [];
    },
    showSquadsSuffix() {
      return this.standingsGroups.length > 1;
    },
    /** One tab per competition that actually has a standings table (non-empty stages). */
    leagueTabs() {
      const tabs = [];
      for (const g of this.standingsGroups) {
        const list = g.competitions || [];
        for (let i = 0; i < list.length; i++) {
          const c = list[i];
          if (!this.stagesWithRows(c).length) {
            continue;
          }
          const name = c.competition_name || `Competition ${c.competition_id ?? i}`;
          const season = c.tournament_season ? ` ${String(c.tournament_season).trim()}` : "";
          let title = `${name}${season}`.trim();
          if (this.showSquadsSuffix) {
            title = `${title} · ${g.label}`;
          }
          tabs.push({
            key: `${g.key}-${c.competition_id ?? "x"}-${c.tournament_id ?? i}`,
            title,
            groupKey: g.key,
            competition: c,
          });
        }
      }
      return tabs;
    },
    hasLeagueTabs() {
      return this.leagueTabs.length > 0;
    },
  },
  created() {
    this.loadStandings();
  },
  methods: {
    formatTeamTypeLabel(key) {
      const map = {
        first_team: "First team",
        women: "Women",
        youth: "Youth",
      };
      if (map[key]) return map[key];
      return String(key)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    stagesWithRows(competition) {
      const stages = competition && Array.isArray(competition.stages) ? competition.stages : [];
      return stages.filter((s) => s.standings && s.standings.length);
    },
    tableStateId(groupKey, competition, stage) {
      const cid = competition.competition_id != null ? competition.competition_id : "";
      const sid = stage.stage_id != null ? stage.stage_id : "";
      return `${groupKey}-${cid}-${sid}`;
    },
    tableCurrentPage(stateId) {
      const p = this.tablePages[stateId];
      return p && p > 0 ? p : 1;
    },
    onTablePageChange(stateId, page) {
      this.$set(this.tablePages, stateId, page);
    },
    competitionTypeLabel(type) {
      if (!type) return "";
      return String(type).replace(/_/g, " ");
    },
    async loadStandings() {
      this.loading = true;
      this.error = null;
      this.standingsPayload = null;
      try {
        const res = await Teams.getSportStandings();
        this.standingsPayload = res.data && res.data.data ? res.data.data : null;
        this.tablePages = {};
        this.tabIndex = 0;
      } catch (e) {
        const msg =
          (e.response && e.response.data && e.response.data.error) ||
          e.message ||
          "Could not load standings";
        this.error = msg;
      } finally {
        this.loading = false;
      }
    },
  },
  middleware: "authentication",
};
</script>

<template>
  <Layout>
    <PageHeader :title="pageTitle" :items="breadcrumbItems" />
    <div class="container-fluid">
      <div v-if="loading" class="text-center py-5">
        <b-spinner variant="primary" label="Loading" />
      </div>

      <div v-else-if="error" class="alert alert-danger" role="alert">
        {{ error }}
      </div>

      <div v-else-if="!hasLeagueTabs" class="alert alert-info" role="alert">
        No standings available.
      </div>

      <b-card v-else no-body>
        <b-tabs
          v-model="tabIndex"
          pills
          card
          lazy
          nav-class="flex-wrap px-2 pt-2"
          content-class="p-0"
        >
          <b-tab
            v-for="tab in leagueTabs"
            :key="tab.key"
            :title="tab.title"
          >
            <div class="p-3">
              <div
                v-for="(stage, sIdx) in stagesWithRows(tab.competition)"
                :key="`${tab.key}-stage-${sIdx}`"
                class="card mb-3"
              >
                  <div class="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <div>
                      <span
                        v-if="tab.competition.competition_type"
                        class="badge bg-secondary me-2 text-capitalize"
                      >
                        {{ competitionTypeLabel(tab.competition.competition_type) }}
                      </span>
                      <span class="text-muted small">
                        {{ stage.stage_name }}
                        <span v-if="stage.last_updated" class="ms-1">
                          · {{ stage.last_updated }}
                        </span>
                        <span class="ms-1">· {{ stage.standings.length }} teams</span>
                      </span>
                    </div>
                  </div>
                  <div class="card-body p-0">
                    <b-table
                      :items="stage.standings"
                      :fields="tableFields"
                      striped
                      hover
                      responsive
                      class="mb-0"
                      sort-by="rank"
                      :sort-desc="false"
                      :per-page="tablePerPage"
                      :current-page="tableCurrentPage(tableStateId(tab.groupKey, tab.competition, stage))"
                    >
                      <template #cell(rank)="row">
                        <span>{{ row.item.rank }}</span>
                        <span
                          v-if="row.item.rank_description"
                          class="text-muted small d-block"
                        >
                          {{ row.item.rank_description }}
                        </span>
                      </template>
                      <template #cell(team)="row">
                        <div class="d-flex align-items-center">
                          <img
                            v-if="row.item.team_logo"
                            :src="row.item.team_logo"
                            alt=""
                            class="rounded me-2 flex-shrink-0"
                            style="width: 28px; height: 28px; object-fit: contain"
                          />
                          <div>
                            <div class="fw-semibold">{{ row.item.team_name }}</div>
                            <div class="text-muted small">{{ row.item.team_short }}</div>
                          </div>
                        </div>
                      </template>
                      <template #cell(goal_difference)="row">
                        {{ row.item.goal_difference }}
                      </template>
                    </b-table>
                    <div
                      v-if="stage.standings.length > tablePerPage"
                      class="d-flex justify-content-center p-2 border-top"
                    >
                      <b-pagination
                        :value="tableCurrentPage(tableStateId(tab.groupKey, tab.competition, stage))"
                        :total-rows="stage.standings.length"
                        :per-page="tablePerPage"
                        size="sm"
                        class="mb-0"
                        @input="onTablePageChange(tableStateId(tab.groupKey, tab.competition, stage), $event)"
                      />
                    </div>
                  </div>
                </div>
            </div>
          </b-tab>
        </b-tabs>
      </b-card>
    </div>
  </Layout>
</template>
